/*
  Shared scroll-rail engine:
    · section-product-ledge   (.modulo-plg__*)
    · section-product-poster  (.modulo-ppo__*)
    · section-product-frame   (.modulo-pfr__*)

  NEUTRAL NAME AND NEUTRAL HOOKS ARE LOAD-BEARING. Naming this after any one
  consumer would collide with that section's own CSS asset filename, and keying
  it off BEM classes would weld one namespace into all of them. It reads
  `data-modulo-ledge` / `data-ledge-*` ONLY, so each consumer keeps its own
  prefix. Edit it with ALL THREE in mind — this is the sr/sd, nm/nw and br/bsl
  precedent. The file keeps its "ledge" name because two sections already
  reference it by that filename on installed merchant themes.

  The rail itself is CSS scroll-snap and is fully usable with this file absent:
  JS only adds arrow stepping and the end-state disabling. Nothing here writes
  layout, so there is no CLS on hydrate.
*/
(function () {
  'use strict';
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  function init(root) {
    if (!root || root.hasAttribute('data-ledge-ready')) return;
    root.setAttribute('data-ledge-ready', '');

    var rail = root.querySelector('[data-ledge-rail]');
    if (!rail) return;
    var prev = root.querySelector('[data-ledge-prev]');
    var next = root.querySelector('[data-ledge-next]');

    /* One card plus one gap is the step. Measured live rather than cached:
       cards-per-view is responsive, so a resize changes it and any cached
       width would step by a stale amount for the rest of the session. */
    function step() {
      var card = rail.querySelector('[data-ledge-card]');
      if (!card) return rail.clientWidth;
      var gap = parseFloat(window.getComputedStyle(rail).columnGap || '0') || 0;
      return card.getBoundingClientRect().width + gap;
    }

    /* RTL: a spec-compliant RTL scroller runs 0 → negative, so the raw value
       cannot be compared against 0 and maxScroll directly. Normalising to a
       magnitude makes both ends testable in either direction (the `bs`/`tsg`
       lesson — probe the model, never sniff it). */
    function pos() { return Math.abs(rail.scrollLeft); }
    function maxScroll() { return rail.scrollWidth - rail.clientWidth; }

    function sync() {
      var at = pos();
      var max = maxScroll();
      /* [hidden] is not used here — these are real buttons that stay in the
         DOM so the rail's controls do not reflow at the ends. */
      if (prev) prev.disabled = at <= 2;
      if (next) next.disabled = at >= max - 2 || max <= 0;
      /* A rail that does not overflow has no controls worth showing. The
         Liquid already predicts the desktop case; this covers the rest. */
      var nav = root.querySelector('[data-ledge-nav]');
      if (nav) nav.classList.toggle('is-idle', max <= 2);
    }

    function go(dir) {
      var d = step() * dir;
      /* Direction-aware: in RTL, "next" means a more negative scrollLeft. */
      if (window.getComputedStyle(rail).direction === 'rtl') d = -d;
      if (typeof rail.scrollBy === 'function') {
        rail.scrollBy({ left: d, behavior: reduced() ? 'auto' : 'smooth' });
      } else {
        rail.scrollLeft += d;
      }
    }

    function reduced() {
      /* A LIVE query, never a boot-time boolean — a mid-session toggle must
         take effect (the `as` lesson). */
      return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    }

    if (prev) prev.addEventListener('click', function () { go(-1); });
    if (next) next.addEventListener('click', function () { go(1); });

    var ticking = false;
    rail.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () { ticking = false; sync(); });
    }, { passive: true });

    var rt;
    window.addEventListener('resize', function () {
      clearTimeout(rt);
      rt = setTimeout(sync, 100);
    });

    /* Webfonts land after a deferred script and change card metrics, so the
       first sync must wait for them where the API exists (the `as`/`hrl`
       lesson). */
    if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
      document.fonts.ready.then(sync);
    }
    sync();
  }

  function boot(scope) {
    var els = (scope || document).querySelectorAll('[data-modulo-ledge]');
    for (var i = 0; i < els.length; i++) init(els[i]);
  }

  /* An identical <script src> still executes once per tag, and two ledge
     sections on one page emit two of them — guard the document listeners or
     every handler is bound twice (the `as`/`hpi` lesson). */
  if (!window.__moduloLedgeWired) {
    window.__moduloLedgeWired = true;
    document.addEventListener('shopify:section:load', function (e) {
      boot(e.target || document);
    });
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { boot(document); });
    }
  }

  if (document.readyState !== 'loading') boot(document);
})();
